import React, {useEffect, useRef, useState} from 'react';
import {Button, Col, Input, PageHeader, Row, Table,} from 'antd';
import {ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import _ from "lodash";
import {getD365ExpenseCodes, getD365ExpenseMappings, getD365ExpenseTypes} from "../../api/d365Api";
import {useUser, useUserPermissions} from "../../auth/userHooks";
import {ExpenseMappingButton} from "./ExpenseMappingButton";
import {RemoveMappingButton} from "./RemoveMappingButton";
import moment from "moment/moment";
import {handleResponse} from "./utils";
import {PREFERRED_DATETIME_FORMAT} from "../../util/time";
import {getPaginationConfig} from "../../util/showAllPagination";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";

const ExpenseMappings = () => {
  const [loading, setLoading] = useState(false);
  const [expenseMappings, setExpenseMappings] = useState([]);
  const [filteredExpenseMappings, setFilteredExpenseMappings] = useState([]);
  const [codes, setCodes] = useState([]);
  const [types, setTypes] = useState([]);
  const permissions = useUserPermissions();
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);
  const user = useUser();
  const columns = [
    {
      title: 'Expense Code ID',
      key: 'ID',
      dataIndex: 'Code',
      sorter: (a, b) => a.Code.localeCompare(b.Code),
      width: '10%',
      render: i => i ? i : '-'
    },
    {
      title: 'Expense Type ID',
      dataIndex: 'Type',
      sorter: (a, b) => a.Type.localeCompare(b.Type),
      width: '30%',
      render: i => i ? i : '-'
    },
    {
      title: 'Created By Email',
      dataIndex: 'created_by_email',
      sorter: (a, b) => a.created_by_email.localeCompare(b.created_by_email),
      width: '20%',
      render: i => i ? i : '-'
    },
    {
      title: 'Created At',
      dataIndex: 'CreatedAt',
      sorter: (a, b) => a.CreatedAt.localeCompare(b.CreatedAt),
      width: '125px',
      render: i => i ? moment.utc(i).local().format(PREFERRED_DATETIME_FORMAT) : '-'
    },
    {
      title: 'Action',
      width: '125px',
      key: 'action',
      render: h => (
        <>
          {permissions !== null && permissions.canManageExpenses &&
            (
              <>
                <Row>
                  <RemoveMappingButton
                    id={h.ID}
                    email={user.email}
                    onCompleted={handleExpenseMappingResponse}/>
                </Row>
              </>
            )
          }
        </>
      ),
    },
  ];

  const filterExpenseMappings = (expenseMappings, searchText) => {
    var lcSearchText = _.toLower(searchText)
    setFilteredExpenseMappings(
      _.chain(expenseMappings)
        .filter(
          (u) =>
            searchText === "" ||
            _.includes(_.toLower(u.Code), lcSearchText) ||
            _.includes(_.toLower(u.Type), lcSearchText)
        )
        .value()
    );
  };

  const search = (text) => {
    filterExpenseMappings(expenseMappings, text);
  };

  const loadExpenseMappings = async () => {
    try {
      setLoading(true);
      const expenseMappings = await getD365ExpenseMappings();
      const expenseCodes = await getD365ExpenseCodes();
      const expenseTypes = await getD365ExpenseTypes();

      setCodes(expenseCodes);
      setTypes(expenseTypes);
      setExpenseMappings(expenseMappings);
      filterExpenseMappings(expenseMappings);
    } catch (e) {
      console.error(`Could not get expense details`);
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseMappingResponse = (response, action) => {
    handleResponse(response, "Expense mapping", action);
    loadExpenseMappings();
  };

  useEffect(() => {
    loadExpenseMappings();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Expense Mappings"
            subTitle={`(${filteredExpenseMappings.length} shown)`}
            style={{padding: 0}}
          />
        </Col>
      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={16} style={{marginRight: 10}}>
          <Input
            style={{maxWidth: 400}}
            placeholder="Search for expense mappings by expense code id"
            prefix={<SearchOutlined/>}
            onChange={(e) => search(e.target.value)}
            allowClear
          />
        </Col>
        <Col flex="auto">
          <Button
            style={{float: 'right'}}
            onClick={() => loadExpenseMappings()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
          {permissions !== null && permissions.canManageExpenses && (
            <>
              <ExpenseMappingButton
                disabled={loading}
                title={"Create Expense Mapping"}
                email={user.email}
                codes={codes}
                types={types}
                onCompleted={handleExpenseMappingResponse}
                style={{marginRight: 10, align: "center", float: 'right'}}
              />
            </>
          )}
        </Col>
      </Row>

      <Table
        ref={tableContainerRef}
        rowKey="ID"
        dataSource={filteredExpenseMappings}
        columns={columns}
        pagination={getPaginationConfig(filteredExpenseMappings.length)}
        size="small"
        loading={loading}
        scroll={{y: tableHeight}}
      />
    </>
  );
};

export {ExpenseMappings};
